import React, {FC, memo} from "react";
import {Button, Select} from "antd";
import {ArrayDataOptions, Maybe} from "../../generated/graphql";
import i18n from "../../services/i18n";

type FilterDropdownSearchMultipleProps = {
    dataIndex: string;
    options?: Maybe<ArrayDataOptions>;
    setOptions?: (options: ArrayDataOptions) => void;
    setDropdownOpen?: (dropdownOpen: string | null) => void;
    setNewFilterOption: (column: string, value?: string | Array<string>) => void;
    resetSelectedFilter: (column: string) => void;
};

// Feste Panelbreite, damit das Tag-Input den Spaltenheader nicht sprengt.
// Tags wachsen vertikal (mehrzeilig), kein horizontaler Overflow.
const PANEL_WIDTH = 240;

// Legacy-Cache-Buster-Token: rein numerischer String mit Länge >= 13
// (Millisekunden-Timestamp aus dem Single-Value-Filter-Pfad). Diese Token
// dürfen NICHT als sichtbare Tags rehydriert werden.
const isLegacyTimestampToken = (value: string): boolean => {
    return value.length >= 13 && /^\d+$/.test(value);
};

/**
 * Bereinigt gespeicherte Filter-Werte für die Anzeige als Tags:
 * entfernt leere Strings und Legacy-Timestamp-Token.
 */
export const sanitizeTagValues = (values: Array<string>): Array<string> => {
    return values.filter((value) => value !== "" && !isLegacyTimestampToken(value));
};

/**
 * Tag-Input-Filter für mehrere Freitext-Werte (z.B. mehrere Kursnummern).
 * Nutzt AntD v4 `<Select mode="tags" />` ohne Options-Popup (open={false}),
 * d.h. reine Freitext-Eingabe mit Komma als Trenner.
 */
const FilterDropdownSearchMultiple: FC<FilterDropdownSearchMultipleProps> = ({
    dataIndex,
    options,
    setNewFilterOption,
    resetSelectedFilter,
}) => {
    const currentFilter = options?.filter?.find((filter) => filter.column === dataIndex);

    // Rehydration aus gespeichertem Filter: Leer- und Legacy-Timestamp-Token
    // herausfiltern, bevor sie als Tags angezeigt werden.
    const initialTags = sanitizeTagValues(currentFilter?.values ?? []);

    const [selectedTags, setSelectedTags] = React.useState<Array<string>>(initialTags);

    const placeholder = `${i18n.views.TableColumnTitles[
        dataIndex as keyof typeof i18n.views.TableColumnTitles
    ]()} eingeben`;

    const hasActiveFilter = (currentFilter?.values ?? []).length > 0;

    // Zweistufige Enter-Bedienung im Tag-Input:
    //  1. Enter bei getipptem Text: rc-select committet den Text dank
    //     mode="tags" + open={false} selbst als Tag und leert das Eingabefeld.
    //  2. Enter bei LEEREM Feld: startet die Filterung (oder entfernt den Filter,
    //     wenn keine Kursnummer mehr eingegeben ist) -- ersetzt den "Suchen"-Klick.
    // So lassen sich mehrere Kursnummern per Enter erfassen und mit einem letzten
    // (leeren) Enter filtern; der "Suchen"-Button bleibt für die Maus-Bedienung.
    const handleInputKeyDown: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
        if (event.key !== "Enter") return;
        // IME-/Composition-Enter nicht als "Feld leer" missdeuten.
        if (event.nativeEvent.isComposing) return;
        // Solange noch etwas im Feld steht (auch reine Leerzeichen), committet
        // rc-select den Eintrag gleich selbst als Tag -> hier nichts tun. Nur das
        // wirklich leere Feld löst Stufe 2 aus (verhindert eine Doppelaktion aus
        // Filtern + Leerzeichen-Tag-Commit).
        if (event.currentTarget.value !== "") return;
        // Feld leer -> ohne Tags den Filter entfernen, sonst filtern.
        if (selectedTags.length === 0) {
            resetSelectedFilter(dataIndex);
            return;
        }
        setNewFilterOption(dataIndex, selectedTags);
    };

    return (
        <div style={{padding: 8, width: PANEL_WIDTH}}>
            <div style={{marginBottom: 8, display: "block"}}>
                <Select
                    mode="tags"
                    tokenSeparators={[","]}
                    open={false}
                    allowClear
                    autoFocus
                    style={{width: "100%"}}
                    placeholder={placeholder}
                    value={selectedTags}
                    onChange={(values) => setSelectedTags(values)}
                    onInputKeyDown={handleInputKeyDown}
                    // Kein Options-Popup -> notFoundContent unsichtbar halten
                    notFoundContent={null}
                />
            </div>
            <Button
                type="primary"
                onClick={() => {
                    setNewFilterOption(dataIndex, selectedTags);
                }}
                size="small"
                style={{width: "100%"}}
                disabled={!selectedTags.length}
            >
                {"Suchen"}
            </Button>
            <Button
                style={{width: "100%", marginTop: "5px"}}
                danger
                size="small"
                disabled={!hasActiveFilter}
                onClick={() => {
                    resetSelectedFilter(dataIndex);
                }}
            >
                {"Filter entfernen"}
            </Button>
        </div>
    );
};

export default memo(FilterDropdownSearchMultiple);
